import { create } from 'zustand'
import type { Permission, Role } from '@tumin/domain'
import { STORAGE_KEYS } from '@/lib/constants'

/** Tema visual elegido por el usuario. */
export type ThemePreference = 'light' | 'dark' | 'system'
/** Densidad de la interfaz: cómoda (táctil) o compacta (escritorio). */
export type DensityPreference = 'comfortable' | 'compact'

/** Preferencias personales guardadas en el servidor (`PATCH /employees/me`). */
export interface EmployeePreferences {
  theme?: ThemePreference | undefined
  density?: DensityPreference | undefined
  /** Identificadores de widgets visibles en el panel; `undefined` = todos. */
  dashboardWidgets?: string[] | undefined
}

/** Usuario autenticado tal como lo devuelve el API (`publicEmployee`). */
export interface SessionEmployee {
  id: string
  fullName: string
  email: string
  role: Role
  branchId: string | null
  phone: string | null
  isActive: boolean
  preferences: EmployeePreferences
  mustChangePassword: boolean
  /** Permisos efectivos del rol (incluye personalizaciones de la institución). */
  permissions: Permission[]
  /** Tiene activa la verificación en dos pasos. */
  twoFactorEnabled: boolean
  /** La política de la institución exige verificación en dos pasos a su rol. */
  twoFactorRequired: boolean
}

/** Respuesta de `/auth/login` y `/auth/refresh`. */
export interface IssuedSession {
  accessToken: string
  refreshToken: string
  /** Vigencia del token de acceso, en segundos. */
  expiresIn: number
  employee: SessionEmployee
}

/**
 * Respuesta de `/auth/login` cuando el usuario tiene verificación en dos
 * pasos: aún no hay sesión, solo un desafío que se canjea con el código en
 * `/auth/login/two-factor`.
 */
export interface TwoFactorChallenge {
  twoFactorRequired: true
  challengeToken: string
  /** Segundos de vigencia del desafío. */
  expiresIn: number
}

/** Resultado del primer paso del login: sesión directa o desafío de segundo factor. */
export type LoginResponse = IssuedSession | TwoFactorChallenge

/** ¿El login pide el segundo factor? */
export function isTwoFactorChallenge(response: LoginResponse): response is TwoFactorChallenge {
  return 'challengeToken' in response
}

/**
 * ¿La política obliga a este usuario a activar la verificación en dos pasos
 * antes de usar la aplicación? (El API también lo impide.)
 */
export function needsTwoFactorEnrollment(employee: Pick<SessionEmployee, 'twoFactorEnabled' | 'twoFactorRequired'> | null): boolean {
  return !!employee && employee.twoFactorRequired && !employee.twoFactorEnabled
}

/**
 * Almacén del token de refresco. Si el usuario marcó "recordar este
 * dispositivo" vive en localStorage (sobrevive al cierre del navegador);
 * si no, en sessionStorage (se borra al cerrar la pestaña). El token de
 * acceso nunca se persiste: solo vive en memoria.
 */
export const refreshTokenStore = {
  /** Devuelve el token de refresco vigente, si existe. */
  get(): string | null {
    return safeRead(localStorage) ?? safeRead(sessionStorage)
  },
  /** ¿El token está guardado en localStorage (dispositivo recordado)? */
  isRemembered(): boolean {
    return safeRead(localStorage) !== null
  },
  /**
   * Guarda un token nuevo. Sin `remember` conserva el almacenamiento actual
   * (caso de la rotación en cada refresco).
   */
  set(token: string, remember?: boolean): void {
    const persist = remember ?? this.isRemembered()
    const target = persist ? localStorage : sessionStorage
    const other = persist ? sessionStorage : localStorage
    try {
      target.setItem(STORAGE_KEYS.refreshToken, token)
      other.removeItem(STORAGE_KEYS.refreshToken)
    } catch {
      /* almacenamiento bloqueado (modo privado): la sesión dura solo en memoria */
    }
  },
  /** Elimina el token de ambos almacenamientos. */
  clear(): void {
    try {
      localStorage.removeItem(STORAGE_KEYS.refreshToken)
      sessionStorage.removeItem(STORAGE_KEYS.refreshToken)
    } catch {
      /* sin acceso al almacenamiento */
    }
  },
}

function safeRead(storage: Storage): string | null {
  try {
    return storage.getItem(STORAGE_KEYS.refreshToken)
  } catch {
    return null
  }
}

interface AuthState {
  /** Token de acceso JWT (solo en memoria). */
  accessToken: string | null
  employee: SessionEmployee | null
  /** Instala una sesión nueva (login) o rotada (refresh). */
  setSession: (session: IssuedSession, remember?: boolean) => void
  /** Reemplaza los datos del usuario (tras editar perfil o preferencias). */
  setEmployee: (employee: SessionEmployee) => void
  /** Olvida la sesión local (no llama al API). */
  clearSession: () => void
}

/** Estado global de autenticación. */
export const useAuthStore = create<AuthState>((set) => ({
  accessToken: null,
  employee: null,
  setSession(session, remember) {
    refreshTokenStore.set(session.refreshToken, remember)
    set({ accessToken: session.accessToken, employee: session.employee })
  },
  setEmployee(employee) {
    set({ employee })
  },
  clearSession() {
    refreshTokenStore.clear()
    set({ accessToken: null, employee: null })
  },
}))

/** Hook de conveniencia: usuario autenticado actual (o `null`). */
export function useCurrentEmployee(): SessionEmployee | null {
  return useAuthStore((s) => s.employee)
}

/** Iniciales para el avatar: "María López" → "ML". */
export function employeeInitials(employee: Pick<SessionEmployee, 'fullName' | 'email'> | null): string {
  const INITIALS_LENGTH = 2
  const fromName = employee?.fullName
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0] ?? '')
    .slice(0, INITIALS_LENGTH)
    .join('')
    .toUpperCase()
  return fromName || employee?.email[0]?.toUpperCase() || '?'
}
